import React, { useEffect, useState } from "react";
import { apiGet } from "api/callApi";
import { Select, Checkbox, ConfigProvider, Tag } from "antd";
import theme from "theme";

const Notificationdropdown = ({
  onChange = () => {},
  defaultValue = "",
  name,
  disabledDropdown = false,
  placeholder = "Select Recipient",
  type,
  projectId,
  width = "40%",
  active,
  disabledValues = [],
  disabledEmails = [],
  maxTagCount = 4,
}) => {
  const isUserDisabled = (user) =>
    disabledValues.includes(user.id) || disabledEmails.includes(user.email);
  const [options, setOptions] = useState([]);
  const [selectedValues, setSelectedValues] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [roleGroups, setRoleGroups] = useState({}); // { roleName: [user, user] }

  useEffect(() => {
    const getData = async () => {
      try {
        const projectIds = Array.isArray(projectId)
          ? projectId.filter(Boolean).join(",")
          : projectId;

        if (!projectIds) return;

        const response = await apiGet(`/v1/addressbook/${projectIds}/${type}`);
        const rawData = response.data;

        const groupedRoles = {};
        const allOptionsMap = {}; // use map to deduplicate

        rawData.forEach((item) => {
          const roles = item.roles ? item.roles.split(",") : ["Others"];
          roles.forEach((role) => {
            const trimmedRole = role.trim() || "Others";
            if (!groupedRoles[trimmedRole]) {
              groupedRoles[trimmedRole] = [];
            }

            const userObj = {
              id: item.id,
              label: item.fullName,
              email: item.email,
              role: trimmedRole,
            };

            groupedRoles[trimmedRole].push(userObj);

            if (!allOptionsMap[item.email]) {
              allOptionsMap[item.email] = userObj; // only first instance
            }
          });
        });

        const allOptions = Object.values(allOptionsMap);

        setOptions(allOptions);
        setRoleGroups(groupedRoles);

        const initialSelected = allOptions.filter((opt) =>
          (defaultValue || "").split(", ").includes(opt.email),
        );
        setSelectedValues(initialSelected);
      } catch (error) {
        console.error("Error fetching options:", error);
      }
    };

    getData();
  }, [projectId, type, defaultValue]);

  const handleRoleSelect = (role, checked) => {
    const users = (roleGroups[role] || []).filter((u) => !isUserDisabled(u));

    let updatedValues = checked
      ? [
          ...selectedValues,
          ...users.filter(
            (u) => !selectedValues.find((s) => s.email === u.email),
          ),
        ]
      : selectedValues.filter((s) => !users.find((u) => u.email === s.email));

    setSelectedValues(updatedValues);

    const emailsString = updatedValues.map((value) => value.email).join(", ");
    onChange(name, emailsString, active);
  };

  const handleUserSelect = (user, checked) => {
    if (isUserDisabled(user)) return;
    const updatedValues = checked
      ? [...selectedValues, user]
      : selectedValues.filter((val) => val.email !== user.email);

    setSelectedValues(updatedValues);

    const emailsString = updatedValues.map((val) => val.email).join(", ");
    onChange(name, emailsString, active);
  };

  const handleSelectChange = (selectedEmails) => {
    const updatedValues = options.filter((opt) =>
      selectedEmails.includes(opt.email),
    );
    setSelectedValues(updatedValues);

    const emailsString = updatedValues.map((value) => value.email).join(", ");
    onChange(name, emailsString, active);
    setSearchText("");
  };

  const handleSearch = (value) => {
    if (value) {
      let tempValue = value.trim();
      setSearchText(tempValue);
    }
  };

  const handleOpenChange = (open) => {
    /* if (!open) {
      setSearchText("");
    } */
  };

  const customTagRender = (props) => {
    const { label, value, closable, onClose } = props;

    const matchedOpt = options.find((opt) => opt.email === value);
    const isDisabled = matchedOpt ? isUserDisabled(matchedOpt) : false;
    if (isDisabled) {
      return (
        <Tag color="#BFBFBF" style={{ marginRight: 3 }}>
          {label}
        </Tag>
      );
    }

    return (
      <Tag closable={closable} onClose={onClose} style={{ marginRight: 3 }}>
        {label}
      </Tag>
    );
  };

  const renderGroupedCheckboxList = () => {
    const searchFilteredGroups = {};

    Object.entries(roleGroups).forEach(([role, users]) => {
      const filteredUsers = users.filter(
        (user) =>
          user.label.toLowerCase().includes(searchText.toLowerCase()) ||
          selectedValues.find((sel) => sel.email === user.email),
      );

      if (filteredUsers.length > 0) {
        searchFilteredGroups[role] = filteredUsers;
      }
    });

    const isEmpty = Object.keys(searchFilteredGroups).length === 0;

    if (isEmpty) {
      return (
        <div style={{ padding: 10, textAlign: "center", color: "#999" }}>
          No Data
        </div>
      );
    }

    return (
      <div
        style={{ maxHeight: "250px", overflowY: "auto" }}
        onMouseDown={(e) => e.preventDefault()}
      >
        {Object.entries(searchFilteredGroups)
          .sort(([roleA], [roleB]) => roleA.localeCompare(roleB))
          .map(([role, users]) => {
            const allChecked = users.every((u) =>
              selectedValues.find((s) => s.email === u.email),
            );

            return (
              <div key={role}>
                <div
                  style={{
                    background: "#e0e0e0",
                    padding: "6px 10px",
                    fontWeight: "bold",
                    borderBottom: "1px solid #ccc",
                  }}
                >
                  <Checkbox
                    checked={allChecked}
                    onChange={(e) => handleRoleSelect(role, e.target.checked)}
                  >
                    {role}
                  </Checkbox>
                </div>
                {users.map((user) => (
                  <div key={user.id}>
                    <Checkbox
                      checked={selectedValues.find(
                        (s) => s.email === user.email,
                      )}
                      disabled={isUserDisabled(user)}
                      onChange={(e) => handleUserSelect(user, e.target.checked)}
                      style={{ display: "flex", padding: "5px 10px" }}
                    >
                      {user.label}
                    </Checkbox>
                  </div>
                ))}
              </div>
            );
          })}
      </div>
    );
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#ED850C",
          colorBorder: theme.colors.bordertask,
          colorText: "black",
          colorTextPlaceholder: "rgba(0, 0, 0, 0.5)",
        },
      }}
    >
      <Select
        filterOption={false}
        mode="multiple"
        value={selectedValues.map((val) => val.email)}
        onChange={handleSelectChange}
        tagRender={customTagRender}
        style={{ width, border: "red" }}
        placeholder={placeholder}
        dropdownRender={() => renderGroupedCheckboxList()}
        disabled={disabledDropdown}
        maxTagCount={maxTagCount}
        onSearch={handleSearch}
        onOpenChange={handleOpenChange}
      >
        {options.map((opt) => (
          <Select.Option key={opt.id} value={opt.email}>
            {opt.label}
          </Select.Option>
        ))}
      </Select>
    </ConfigProvider>
  );
};

export default Notificationdropdown;
